import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';

interface StandardCardProps extends HTMLMotionProps<"div"> {
  variant?: 'default' | 'elevated' | 'outlined' | 'ghost';
  padding?: 'none' | 'small' | 'medium' | 'large';
  fullHeight?: boolean;
  hoverable?: boolean;
  selected?: boolean;
}

export const StandardCard: React.FC<StandardCardProps> = ({
  children,
  variant = 'default',
  padding = 'medium',
  fullHeight = false,
  hoverable = false,
  selected = false,
  className = '',
  ...props
}) => {
  const { isDark } = useTheme();

  const baseClasses = 'rounded-xl transition-all duration-200';
  
  const paddingClasses = {
    none: '',
    small: 'p-4',
    medium: 'p-6',
    large: 'p-8'
  };

  // Aurora dark mode: deep purple backgrounds with violet accent borders
  const variantClasses = {
    default: isDark
      ? 'bg-gradient-to-br from-dark-secondary to-dark-accent border border-aurora-violet-500/20 shadow-aurora-card'
      : 'bg-white border border-gray-200 shadow-sm',

    elevated: isDark
      ? 'bg-gradient-to-br from-dark-elevated to-dark-accent shadow-aurora-glow backdrop-blur-md border border-aurora-violet-500/10'
      : 'bg-white shadow-lg',

    outlined: isDark
      ? 'bg-transparent border-2 border-aurora-violet-500/30'
      : 'bg-transparent border-2 border-gray-300',

    ghost: 'bg-transparent'
  };

  // Aurora hover: violet/cyan glow intensifies
  const hoverClasses = hoverable
    ? isDark
      ? 'hover:border-aurora-violet-500/40 hover:shadow-aurora-violet cursor-pointer hover:bg-dark-elevated/50'
      : 'hover:shadow-md hover:border-gray-300 cursor-pointer'
    : '';

  // Aurora selected: vibrant violet highlight
  const selectedClasses = selected
    ? isDark
      ? 'border-aurora-violet-500 bg-aurora-violet-900/20 shadow-aurora-violet'
      : 'border-professional-blue-500 bg-professional-blue-50'
    : '';

  const heightClass = fullHeight ? 'h-full' : '';

  return (
    <motion.div
      className={`${baseClasses} ${paddingClasses[padding]} ${variantClasses[variant]} ${hoverClasses} ${selectedClasses} ${heightClass} ${className}`}
      whileHover={hoverable ? { y: -2 } : {}}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export default StandardCard;